import { RefObject, useState, useCallback } from "react";
import {
  appAreaWidth,
  dropdownMaxHeight,
  DROPDOWN_EDGE,
  DROPDOWN_MAX_HEIGHT,
  fixedFrameOffset,
  visibleBand,
} from "@/lib/layout/appArea";

type SupportedElement =
  | HTMLInputElement
  | HTMLDivElement
  | HTMLButtonElement
  | HTMLLabelElement
  | HTMLParagraphElement
  | HTMLSpanElement;

type ParentPositionOptions = {
  /**
   * Width the anchored menu wants, in px. `left` is clamped so a menu this
   * wide still fits on screen. Defaults to the anchor's own width, which makes
   * the clamp a no-op for menus that are no wider than their trigger.
   */
  desiredWidth?: number;
  /**
   * Height the anchored box wants, in px — for a PANEL that knows how tall it
   * is, not for an option list. Without it the box is a dropdown: it opens
   * below, flips above when the room below is short, and is capped at
   * {@link DROPDOWN_MAX_HEIGHT} either way.
   *
   * With it the box is placed where all of it is visible: below the anchor if
   * it fits there, else above if it fits there, else SHIFTED — pinned to the
   * bottom edge of the visible band, over the anchor, as tall as the band
   * allows. A panel that could show everything and instead scrolls inside a
   * 240px window is what this replaces: with the companion rows switched on,
   * the seat panel in the guest list had to be scrolled on every single row.
   */
  desiredHeight?: number;
};

/** Minimum distance the menu keeps from the viewport's left/right edge. */
const EDGE_MARGIN = 8;

export function useParentPosition(options?: ParentPositionOptions) {
  const desiredWidth = options?.desiredWidth;
  const desiredHeight = options?.desiredHeight;
  const [dropdownPosition, setDropdownPositionState] = useState({
    top: 0,
    left: 0,
    showAbove: false,
    /** How tall the menu may be without leaving the visible band. */
    maxHeight: DROPDOWN_MAX_HEIGHT,
    /**
     * CSS `top` for a fixed box that should cover exactly what is visible.
     * Paired with `bandHeight` — an overlay given both edges cannot be
     * misplaced, whereas the `bottom` this replaced went NEGATIVE with the
     * keyboard up and pushed the sheet down behind it.
     */
    bandTop: 0,
    /**
     * Height of the visible band. A sheet sizes itself against THIS, not
     * against a `dvh` unit: `dvh` is the viewport with the browser's toolbars
     * COLLAPSED — the largest it ever gets — so a `70dvh` cap is more than 70%
     * of what a Safari user can actually see while its bottom bar is out.
     */
    bandHeight: 0,
  });

  const setDropdownPosition = useCallback(
    (inputRef: RefObject<SupportedElement | null>) => {
      const ref = inputRef.current;
      if (ref) {
        const rect = ref.getBoundingClientRect();
      // Two frames, deliberately kept apart.
      //
      // Everything is REASONED in rect coordinates — the frame
      // `getBoundingClientRect()` and `visibleBand()` both speak — and only the
      // emitted numbers are converted, because they are consumed as CSS on a
      // `position: fixed` box.
      //
      // The conversion is not cosmetic. With the iOS keyboard up, fixed boxes
      // stop clinging to the viewport and anchor to the document: measured on
      // an iPhone, a probe at `top: 0` reported a rect top of -295 with
      // `scrollY` at 295. Emitting a rect-derived coordinate straight out put
      // the list 295px above where it belonged — the gap that appeared over
      // dropdowns — and it opened DOWNWARD as well, because a band read in the
      // wrong frame claimed 307px of room below a field that had 12.
        const layoutHeight = window.innerHeight;
        const band = visibleBand();
        const frame = fixedFrameOffset();
        // App area, not the raw viewport: a menu clamped to innerWidth happily
        // positions itself under the docked Velo rail (z-10000, opaque).
        const viewportWidth = appAreaWidth();

        // Horizontal clamp. Without it a trigger near the right edge (or, on a
        // phone, almost any trigger) places the menu partly offscreen, and the
        // page cannot be scrolled to reach it: html carries overflow-x:hidden,
        // so the overhang is clipped away rather than reachable.
        const menuWidth = Math.min(
          desiredWidth ?? rect.width,
          Math.max(0, viewportWidth - 2 * EDGE_MARGIN),
        );
        const maxLeft = Math.max(EDGE_MARGIN, viewportWidth - menuWidth - EDGE_MARGIN);
        const left =
          Math.min(Math.max(rect.left, EDGE_MARGIN), maxLeft) + frame.x;

        const spaceBelow = band.bottom - rect.bottom;
        const spaceAbove = rect.top - band.top;
        // Whether the box has a height of its own (a panel) or is a dropdown.
        // `> 0` rather than `!= null`: a panel measures 0 before its first
        // paint, and 0 must mean "not known yet", not "fits anywhere".
        const wantsHeight =
          desiredHeight !== undefined && desiredHeight > 0 ? desiredHeight : null;
        // The room each side offers to the box itself, edge margin already
        // taken off — the same subtraction `dropdownMaxHeight` makes, kept in
        // one place so "fits" and "how tall" cannot disagree.
        const roomBelow = spaceBelow - DROPDOWN_EDGE;
        const roomAbove = spaceAbove - DROPDOWN_EDGE;
        const shouldShowAbove =
          wantsHeight === null
            ? spaceBelow < DROPDOWN_MAX_HEIGHT && spaceAbove > spaceBelow
            : roomBelow < wantsHeight && roomAbove >= wantsHeight;
        // Neither side has room for the whole panel: slide it up along the
        // band instead of cutting it. Only a box that knows its height can be
        // slid — a dropdown's list grows with its options and keeps flipping.
        const shouldShift =
          wantsHeight !== null && roomBelow < wantsHeight && !shouldShowAbove;

        // Computed here rather than in the consumers: this is the only place
        // that holds the anchor rect and the band in the same frame, and the
        // two components that used to work it out for themselves had drifted
        // into different answers.
        // Where a full-screen overlay has to start and how tall it is, so that
        // it covers exactly the visible strip. Both in the fixed frame.
        //
        // `band.top` came back through `fixedFrameOffset` to be comparable with
        // an anchor rect; adding it again returns `visualViewport.offsetTop`,
        // which is the whole recipe. Written out rather than shortened, so the
        // two directions stay visible next to each other.
        const bandTop = band.top + frame.y;
        const bandHeight = band.height;

        const maxHeight = shouldShift
          ? Math.max(
              0,
              Math.min(wantsHeight as number, band.height - 2 * DROPDOWN_EDGE),
            )
          : dropdownMaxHeight(
              shouldShowAbove ? rect.top : rect.bottom,
              shouldShowAbove,
              band,
              wantsHeight ?? DROPDOWN_MAX_HEIGHT,
            );

        // Shifted: the panel's BOTTOM edge sits one margin above the band's
        // bottom, and its top never leaves the band either (a band shorter
        // than the panel wins — the panel is capped and scrolls). Consumed as
        // CSS `top`, so it is converted into the fixed frame like the others.
        const shiftedTop =
          Math.max(band.top + DROPDOWN_EDGE, band.bottom - DROPDOWN_EDGE - maxHeight) +
          frame.y;

        const newPosition = shouldShift
          ? {
              top: shiftedTop,
              left,
              showAbove: false,
              maxHeight,
              bandTop,
              bandHeight,
            }
          : shouldShowAbove
          ? {
              // Consumed as CSS `bottom`, hence measured from the layout
              // viewport's bottom edge — in the FIXED frame.
              top: layoutHeight - (rect.top + frame.y),
              left,
              showAbove: true,
              maxHeight,
              bandTop,
              bandHeight,
            }
          : {
              top: rect.bottom + frame.y,
              left,
              showAbove: false,
              maxHeight,
              bandTop,
              bandHeight,
            };

        // Only update if position actually changed
        setDropdownPositionState((prev) => {
          if (
            prev.top === newPosition.top &&
            prev.left === newPosition.left &&
            prev.showAbove === newPosition.showAbove &&
            prev.maxHeight === newPosition.maxHeight &&
            prev.bandTop === newPosition.bandTop &&
            prev.bandHeight === newPosition.bandHeight
          ) {
            return prev; // Return previous state to prevent re-render
          }
          return newPosition;
        });
      }
    },
    [desiredWidth, desiredHeight],
  );

  return {
    dropdownPosition,
    setDropdownPosition,
  };
}
